/**
 * Um livro na lista de leitura de uma pessoa.
 *
 * É o `BookItem` de `lib/features/books/data/models/book_item.dart`, com as
 * mesmas chaves gravadas: os dois apps leem e escrevem a mesma coleção
 * (`read_list`), então um nome de campo trocado aqui é um livro perdido lá.
 *
 * O identificador é a "obra" do Open Library (`/works/OL45804W`), e não a
 * edição: edição se multiplica, obra é uma só.
 */

import { traduzir } from '../i18n/idioma';
import { gravarData, lerData } from '../nucleo/tempo';
import type { Casa } from '../types/casa';

export const COLECAO_LEITURA = 'read_list';

export interface Livro {
  id: string;
  /** `workKey`: a obra no Open Library, `/works/OL45804W`. */
  obra: string;
  /** `editionKey`: a edição, quando alguma foi escolhida. */
  edicao: string;
  /** O item no Internet Archive; `pg1342` quando é do Projeto Gutenberg. */
  iaId: string;
  titulo: string;
  autor: string;
  capa: string;
  ano: string;
  paginas: number;
  assuntos: string[];
  /** `to-read`, `read`, `read-self`, `read-both` ou `read-<slug>`. */
  status: string;
  /** De quem é a fila. A `read_list` é por pessoa. */
  quem: string;
  quando: Date;
  /** Onde ler. Resolvido na busca e gravado junto, para não redescobrir. */
  fonte: string;
  /** Que acervo é a fonte: "Project Gutenberg", "Internet Archive"... */
  rotuloDaFonte: string;
}

/**
 * `BookItem.isRead`: qualquer `read-<slug>` conta. A casa não tem mais duas
 * pessoas fixas, então enumerar os nomes aqui deixaria de fora quem chegasse
 * depois.
 */
export const estaLido = (status: string): boolean =>
  status === 'read' || status.startsWith('read-');

/** `BookItem.isToRead`. Só o literal, como lá. */
export const paraLer = (status: string): boolean => status === 'to-read';

/**
 * `BookItem.readDisplay`. O nome sai da casa, nunca do código.
 *
 * O `status` que entra continua cru — é o que está gravado. Só o que sai vira
 * fala, e o nome da pessoa entra como buraco, nunca como verbete.
 */
export function rotuloDeLeitura(status: string, casa?: Casa | null): string {
  if (status === 'read' || status === 'read-both') return traduzir('Todo mundo leu');
  if (status === 'read-self') return traduzir('Lido');
  if (status.startsWith('read-')) {
    const slug = status.slice('read-'.length);
    const pessoa = casa?.pessoas.find((p) => p.slug === slug);
    return pessoa ? traduzir('{quem} leu', { quem: pessoa.nome }) : traduzir('Lido');
  }
  return traduzir('Para ler');
}

/** `BookItem.coverFromCoverId`. */
export function capaPorId(coverId?: number | null, tamanho = 'L'): string {
  if (coverId == null || coverId <= 0) return '';
  return `https://covers.openlibrary.org/b/id/${coverId}-${tamanho}.jpg`;
}

/** `BookItem.coverFromOlid`. */
export function capaPorOlid(olid?: string | null, tamanho = 'L'): string {
  if (!olid) return '';
  return `https://covers.openlibrary.org/b/olid/${olid}-${tamanho}.jpg`;
}

/**
 * `BookItem.deriveReadSourceUrl`: para onde o botão "abrir no navegador"
 * aponta quando o livro não trouxe endereço gravado.
 *
 * Para item do Internet Archive que não é Gutenberg, o endereço é a **página
 * do item**, e não o `_djvu.txt`: livro moderno lá é de empréstimo, o arquivo
 * de texto só existe durante o empréstimo, e apontar para ele abriria um 404.
 */
export function derivarFonte(iaId: string, obra: string): string {
  if (iaId.startsWith('pg') && iaId.length > 2) {
    const id = iaId.slice(2);
    return `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt`;
  }
  if (iaId) return `https://archive.org/details/${iaId}`;
  if (obra) return `https://openlibrary.org${obra}`;
  return '';
}

/**
 * `_looksLikePlainText`: este endereço é o texto do livro, e não a página dele.
 *
 * Mora aqui, e não no `leitura.ts` onde é mais usado, porque o `resultado.ts`
 * também precisa dele para escolher a fonte — e `resultado` não pode importar
 * `leitura` sem fechar um ciclo pelo `acervos`.
 */
export function pareceTextoPuro(url: string): boolean {
  const baixo = url.toLowerCase();
  return baixo.endsWith('.txt') || baixo.includes('gutenberg.org');
}

/** `BookItem.deriveReadSourceLabel`. */
export function derivarRotuloDaFonte(iaId: string): string {
  if (iaId.startsWith('pg')) return 'Project Gutenberg';
  if (iaId) return 'Internet Archive';
  return 'Open Library';
}

/** `BookItem.fromMap`: o que falta vira vazio, e a fonte é redescoberta. */
export function livroDeMapa(id: string, dados: Record<string, unknown>): Livro {
  const texto = (v: unknown): string => (typeof v === 'string' ? v : '');
  const iaId = texto(dados.iaId);
  const obra = texto(dados.workKey);
  return {
    id,
    obra,
    edicao: texto(dados.editionKey),
    iaId,
    titulo: texto(dados.title),
    autor: texto(dados.author),
    capa: texto(dados.coverUrl),
    ano: texto(dados.year),
    paginas: Number(dados.pageCount ?? 0) || 0,
    assuntos: Array.isArray(dados.subjects) ? dados.subjects.map((a) => String(a)) : [],
    status: texto(dados.status) || 'to-read',
    quem: texto(dados.userName),
    quando: lerData(dados.addedAt) ?? new Date(),
    fonte: texto(dados.readSourceUrl) || derivarFonte(iaId, obra),
    rotuloDaFonte: texto(dados.readSourceLabel) || derivarRotuloDaFonte(iaId),
  };
}

/** `BookItem.toMap`, com os nomes que o Flutter gravou. */
export function livroParaMapa(livro: Omit<Livro, 'id'>): Record<string, unknown> {
  return {
    workKey: livro.obra,
    editionKey: livro.edicao,
    iaId: livro.iaId,
    title: livro.titulo,
    author: livro.autor,
    coverUrl: livro.capa,
    year: livro.ano,
    pageCount: livro.paginas,
    subjects: livro.assuntos,
    status: livro.status,
    userName: livro.quem,
    addedAt: gravarData(livro.quando),
    readSourceUrl: livro.fonte,
    readSourceLabel: livro.rotuloDaFonte,
  };
}

/** Um livro vazio, para quem monta um a partir de uma busca. */
export function livroNovo(campos: Partial<Livro> = {}): Livro {
  return {
    id: '',
    obra: '',
    edicao: '',
    iaId: '',
    titulo: '',
    autor: '',
    capa: '',
    ano: '',
    paginas: 0,
    assuntos: [],
    status: '',
    quem: '',
    quando: new Date(),
    fonte: '',
    rotuloDaFonte: '',
    ...campos,
  };
}
